import { supabase } from "@/integrations/supabase/client";
import { isMobileDevice } from "@/utils/deviceUtils";
import { safeStorage } from "@/utils/safariStorage";

export interface SessionData {
  id: string;
  session_token: string;
  device_type: string;
  browser: string;
  os: string;
  ip_address: string;
  location: string;
  last_active: string;
  is_current: boolean;
}

// In-memory fallback for session ID when storage fails
let memorySessionId: string | null = null;

// Generate or retrieve unique session identifier for this browser
const getOrCreateSessionId = (): string => {
  const SESSION_ID_KEY = 'godyspo_session_id';
  
  try {
    let sessionId = safeStorage.getItem(SESSION_ID_KEY);
    
    if (!sessionId) {
      // Check memory fallback
      if (memorySessionId) {
        return memorySessionId;
      }
      
      // Generate unique session ID: UUID-like format + timestamp
      sessionId = `${crypto.randomUUID()}-${Date.now()}`;
      safeStorage.setItem(SESSION_ID_KEY, sessionId);
      memorySessionId = sessionId;
    }
    
    return sessionId;
  } catch (error) {
    console.warn('Error accessing session ID storage:', error);
    // Return memory fallback or generate new
    if (!memorySessionId) {
      memorySessionId = `${crypto.randomUUID()}-${Date.now()}`;
    }
    return memorySessionId;
  }
};

// Get current session ID for comparison
export const getCurrentSessionId = (): string => {
  return getOrCreateSessionId();
};

// Detect browser
export const getBrowserName = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf("Firefox") > -1) return "Firefox";
  if (userAgent.indexOf("Chrome") > -1 && userAgent.indexOf("Edg") === -1) return "Chrome";
  if (userAgent.indexOf("Safari") > -1 && userAgent.indexOf("Chrome") === -1) return "Safari";
  if (userAgent.indexOf("Edg") > -1) return "Edge";
  if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) return "Opera";
  return "Unknown";
};

// Detect OS
export const getOperatingSystem = (): string => {
  const userAgent = navigator.userAgent;
  if (userAgent.indexOf("Win") > -1) return "Windows";
  if (userAgent.indexOf("Mac") > -1) return "macOS";
  if (userAgent.indexOf("Linux") > -1) return "Linux";
  if (userAgent.indexOf("Android") > -1) return "Android";
  if (userAgent.indexOf("iOS") > -1 || userAgent.indexOf("iPhone") > -1) return "iOS";
  return "Unknown";
};

// Track current session
export const trackSession = async () => {
  try {
    const sessionId = getOrCreateSessionId();
    
    const deviceInfo = {
      sessionId,
      deviceType: isMobileDevice() ? "mobile" : "desktop",
      browser: getBrowserName(),
      os: getOperatingSystem(),
    };

    const { data, error } = await supabase.functions.invoke("track-session", {
      body: { deviceInfo },
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error tracking session:", error);
    throw error;
  }
};

// Fetch all active sessions
export const fetchActiveSessions = async (): Promise<SessionData[]> => {
  try {
    const currentSessionId = getCurrentSessionId();
    
    const { data, error } = await supabase.functions.invoke("get-sessions", {
      body: { currentSessionId },
    });

    if (error) throw error;
    return data.sessions || [];
  } catch (error) {
    console.error("Error fetching sessions:", error);
    throw error;
  }
};

// Revoke a specific session
export const revokeSession = async (sessionId: string) => {
  try {
    const { data, error } = await supabase.functions.invoke("revoke-session", {
      body: { sessionId },
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error revoking session:", error);
    throw error;
  }
};

// Revoke all other sessions
export const revokeAllOtherSessions = async () => {
  try {
    const { data, error } = await supabase.functions.invoke("revoke-session", {
      body: { revokeAll: true },
    });

    if (error) throw error;
    return data;
  } catch (error) {
    console.error("Error revoking all sessions:", error);
    throw error;
  }
};

// Delete current session (called during logout)
export const deleteCurrentSession = async (): Promise<void> => {
  try {
    const sessionId = getCurrentSessionId();
    
    const { error } = await supabase.functions.invoke("delete-session", {
      body: { sessionToken: sessionId },
    });

    if (error) throw error;
  } catch (error) {
    console.error("Error deleting current session:", error);
    throw error;
  }
};